import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';
import { SERVER_URL } from '../../../../../Config/config';
import axiosInstance from '../../../../../store/axiosInstance';

export const fetchMeetings = createAsyncThunk('validation/fetchMeetings', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`${SERVER_URL}/api/v1/validation/meetings`);
    return response.data.meetings;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to fetch meetings');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const createMeeting = createAsyncThunk('validation/createMeeting', async (meetingData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/validation/meetings`, meetingData);
    toast.success('Meeting scheduled successfully');
    return response.data.meeting;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to create meeting');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const updateMeeting = createAsyncThunk('validation/updateMeeting', async ({ id, meetingData }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(`${SERVER_URL}/api/v1/validation/meetings/${id}`, meetingData);
    toast.success('Meeting updated successfully');
    return response.data.meeting;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to update meeting');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const deleteMeeting = createAsyncThunk('validation/deleteMeeting', async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${SERVER_URL}/api/v1/validation/meetings/${id}`,);
    toast.success('Meeting deleted successfully');
    return id;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to delete meeting');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchResults = createAsyncThunk('validation/fetchResults', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`${SERVER_URL}/api/v1/validation/results`,);
    return response.data.results;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to fetch results');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchResultsId = createAsyncThunk('validation/fetchResultsId', async (id, { rejectWithValue }) => {
  try {
    console.log('fetchResultsId id', id);
    const response = await axiosInstance.get(`${SERVER_URL}/api/v1/validation/results/${id}`);
    console.log('fetchResultsId response', response);
    return response.data.results;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to fetch results');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const createResult = createAsyncThunk('validation/createResult', async (resultData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`${SERVER_URL}/api/v1/validation/results`, resultData);
    toast.success('Result created successfully');
    return response.data.result;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to create result');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const updateResult = createAsyncThunk('validation/updateResult', async ({ id, resultData }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(`${SERVER_URL}/api/v1/validation/results/${id}`, resultData);
    toast.success('Result updated successfully');
    return response.data.result;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to update result');
    return rejectWithValue(error.response?.data?.message);
  }
});

export const deleteResult = createAsyncThunk('validation/deleteResult', async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${SERVER_URL}/api/v1/validation/results/${id}`);
    toast.success('Result deleted successfully');
    return id;
  } catch (error) {
    toast.error(error.response?.data?.message || 'Failed to delete result');
    return rejectWithValue(error.response?.data?.message);
  }
});

const validationSlice = createSlice({
  name: 'validation',
  initialState: {
    meetings: [],
    userResults: [],
    results: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeetings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.meetings.push(action.payload);
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        const index = state.meetings.findIndex((m) => m.id === action.payload.id);
        if (index !== -1) {
          state.meetings[index] = action.payload;
        }
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        state.meetings = state.meetings.filter((m) => m.id !== action.payload);
      })
      .addCase(fetchResults.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload;
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchResultsId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResultsId.fulfilled, (state, action) => {
        state.loading = false;
        state.userResults = action.payload;
      })
      .addCase(fetchResultsId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createResult.fulfilled, (state, action) => {
        state.results.push(action.payload);
      })
      .addCase(updateResult.fulfilled, (state, action) => {
        const index = state.results.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.results[index] = action.payload;
        }
      })
      .addCase(deleteResult.fulfilled, (state, action) => {
        state.results = state.results.filter((r) => r.id !== action.payload);
      });
  },
});

export default validationSlice.reducer;